import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import clsx from "clsx";
import React, { Children } from "react";

import { Icon } from "../Icon";

type AccordionPropsType = {
  children: React.ReactElement[];
  className?: string;
  defaultOpen?: boolean;
  icon?: Parameters<typeof Icon>[0]["name"];
  isBorderless?: boolean;
  noBottomBorder?: boolean;
  noHoverBg?: boolean;
  onLabelClick?: (isOpen: boolean) => void;
  panelClassName?: string;
};

export default function AccordionV2(props: AccordionPropsType) {
  let _label: React.ReactElement, _content: React.ReactElement;

  Children.forEach(props.children, (child) => {
    if (child!.type === AccordionLabel) {
      _label = child;
    }
    if (child!.type === AccordionContent) {
      _content = child;
    }
  });

  return (
    <div
      className={clsx(
        "w-full max-w-full rounded-md border border-gray-300 relative",
        props.isBorderless && "border-none",
        props.className,
      )}
    >
      <Disclosure defaultOpen={props.defaultOpen}>
        {({ open }) => (
          <>
            <div className="w-full">
              <DisclosureButton
                className="w-full"
                onClick={() => props.onLabelClick && props.onLabelClick!(!open)}
              >
                <div
                  className={clsx(
                    "flex items-center justify-between text-gray-900 sm:text-sm transition-colors duration-200 w-full",
                    !props.noHoverBg && "hover:bg-gray-50",
                    props.isBorderless ? "px-0" : "px-3",
                    props.noBottomBorder
                      ? "py-0"
                      : props.isBorderless
                        ? "py-3"
                        : "py-2",
                    props.isBorderless && !props.noBottomBorder && !open
                      ? "border-b border-gray-200 pr-2"
                      : "border-none",
                  )}
                >
                  <div className={clsx("flex items-center gap-2 w-full")}>
                    {props.icon && (
                      <Icon
                        name={props.icon}
                        className="h-4 w-4 text-gray-400"
                      />
                    )}
                    {_label}
                  </div>
                  <Icon
                    className={clsx(
                      open && "rotate-180",
                      "h-3 w-3 text-gray-400 duration-200 absolute right-2",
                    )}
                    name="chevronDown"
                  />
                </div>
              </DisclosureButton>
            </div>
            <DisclosurePanel>
              <div
                className={clsx(
                  "text-gray-500",
                  props.isBorderless
                    ? "p-0"
                    : "border-t border-gray-300 p-4 pt-0",
                  props.isBorderless && !props.noBottomBorder && open
                    ? "border-b border-gray-200"
                    : "border-none",
                  props.panelClassName,
                )}
              >
                {_content}
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  );
}

const AccordionLabel = ({
  children,
  labelClassName,
  textAlign = "center",
  textLight,
}: {
  children: React.ReactNode;
  labelClassName?: string;
  textAlign?: "center" | "left";
  textLight?: boolean;
}) => {
  return (
    <div
      className={clsx(
        "text-sm flex-1",
        !textLight && "font-medium text-gray-900",
        textAlign === "left" && "text-left",
        labelClassName,
      )}
    >
      {children}
    </div>
  );
};

const AccordionContent = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
);

AccordionV2.Label = AccordionLabel;
AccordionV2.Content = AccordionContent;
